import React from 'react'
import {
  Card,
  CardContent,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  Grid
} from '@mui/material'

const GamifiedDistrictCard = ({ district, onClick }) => {
  // Get color based on rank
  const getCardColor = (rank) => {
    if (rank === 1) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    if (rank === 2) return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    if (rank === 3) return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    if (rank <= 5) return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    if (rank <= 10) return 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)' // Orange gradient
    return 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)' // Slate gray
  }

  const cardBg = getCardColor(district.rank)

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: '280px',
        background: cardBg,
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        }
      }}
      onClick={onClick}
    >
      {/* Rank Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          left: -10,
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '1.2rem',
          color: '#333',
          boxShadow: 3,
          zIndex: 1
        }}
      >
        {district.medal || `#${district.rank}`}
      </Box>

      <CardContent sx={{ pt: 4, pl: 3, pr: 3, pb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {/* Main Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mr: 1 }}>
                {district.district_name}
              </Typography>
              <Tooltip title={district.badge.label}>
                <Chip
                  icon={<span style={{ fontSize: '1.2rem' }}>{district.badge.icon}</span>}
                  label={district.badge.label}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    fontWeight: 'bold',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </Tooltip>
            </Box>
            
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Region: {district.region_name}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={`${district.tempo} Tempo`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(5px)'
                }}
              />
              <Chip
                icon={<span>{district.tier.icon}</span>}
                label={district.tier.name}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(5px)'
                }}
              />
              <Chip
                label={`${district.salon_count} salonów`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(5px)'
                }}
              />
            </Box>
          </Grid>

          {/* Stats */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {district.total_sales.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                sztuk sprzedanych
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  vs średnia:
                </Typography>
                <Chip
                  label={`${district.vsAverage > 0 ? '+' : ''}${district.vsAverage}%`}
                  size="small"
                  sx={{
                    backgroundColor: district.vsAverageNum >= 0 ? 'rgba(76, 175, 80, 0.8)' : 'rgba(244, 67, 54, 0.8)',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Progress Bar */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Pozycja względem lidera
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {district.progressPercent.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={district.progressPercent}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'white',
                    borderRadius: 5,
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default GamifiedDistrictCard

